import { createAsyncThunk } from '@reduxjs/toolkit';

import axiosInstance, { AxiosHeaders } from 'utils/axios';
import type {
    AdminDirectoryOption,
    AdminUser,
    AdminUserSupervisorEmbed,
    CreateAdminStaffResponse,
    CreateUserPayload,
    FetchAdminUsersParams,
    ImportStaffCsvPayload,
    ImportStaffCsvResponse,
    OrganizationKey,
    ReinviteStaffPayload,
    UpdateUserPayload,
    UserUnitOption,
    UserUnitRange
} from '@/types/user-admin';
import { getAdminUserDisplayName } from '@/utils/admin-user';
import { getErrorMessage } from 'utils/error-message';
import { toE164, toE164ForStorage } from 'utils/us-phone-format';
import { DEFAULT_ROWS_PER_PAGE } from '@/constant';

type TKeyValueDescription = {
    id: string;
    key?: string;
    value?: string;
    description?: string | Record<string, unknown>;
};
type AdminStaffUnitItem = {
    id: string;
    hospitalId?: string;
    hospitalName?: string;
    unitCode?: string;
    unitName?: unknown;
    description?: unknown;
    isActive?: boolean;
};
type AdminStaffUnitRangeItem = {
    id: string;
    hospitalId?: string;
    hospitalName?: string;
    unitId?: string;
    unitFloor?: string;
    unitName?: unknown;
    roomStart?: number;
    roomEnd?: number;
    roomRange?: string;
    displayLabel?: string;
    isActive?: boolean;
};
type AdminStaffListItem = {
    id: string;
    units?: AdminStaffUnitItem[] | string;
    unitRanges?: AdminStaffUnitRangeItem[];
    email?: string;
    firstName?: string;
    lastName?: string;
    nickName?: string | null;
    isInternal?: boolean;
    organizationKey?: OrganizationKey;
    organization?: TKeyValueDescription;
    displayName?: string;
    hospitalId?: string;
    role?: TKeyValueDescription;
    jobTitle?: string | { value?: string } | null;
    department?: string | { value?: string } | null;
    scheduleType?: TKeyValueDescription;
    hospitals?: Array<{
        id?: string;
        name?: string;
        isDefault?: boolean;
        isActive?: boolean;
    }>;
    source?: string;
    mobilePhone?: string | null;
    officePhone?: string | null;
    supervisorUserId?: string | null;
    supervisor?: {
        id: string;
        email?: string;
        firstName?: string;
        lastName?: string;
        displayName?: string;
    } | null;
    status?: string;
    isActive?: boolean;
    activeSince?: string;
};

type AdminStaffListResponse = {
    items: AdminStaffListItem[];
    total: number;
    page: number;
    limit: number;
};
type AdminStaffDetailResponse = {
    item?: AdminStaffListItem;
};

type AdminDirectoryResponse = {
    items?: AdminDirectoryOption[];
};

const resolveObjectValue = (value: unknown): string | undefined => {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed || undefined;
    }
    if (value && typeof value === 'object' && 'value' in value) {
        const resolved = (value as { value?: unknown }).value;
        return typeof resolved === 'string' && resolved.trim() ? resolved.trim() : undefined;
    }
    return undefined;
};

const resolvePhoneE164 = (value: unknown): string | null => {
    const raw = resolveObjectValue(value);
    if (!raw) {
        return null;
    }
    return toE164(raw) || null;
};

const phonePayloadToE164 = (value?: string | null): string | undefined => {
    if (!value?.trim()) {
        return undefined;
    }
    return toE164ForStorage(value) || undefined;
};

const normalizeOrganizationToken = (value?: string): string => (value ?? '').trim().replaceAll(' ', '_').toUpperCase();

const resolveOrganizationKey = (item: AdminStaffListItem): OrganizationKey => {
    const raw = item.organizationKey ?? item.organization?.key ?? (item.isInternal ? 'SILVERSTAY' : undefined);
    const normalized = normalizeOrganizationToken(raw);
    if (normalized === 'SILVERSTAY') {
        return 'SILVERSTAY';
    }
    if (normalized === 'HOSPITAL') {
        return 'HOSPITAL';
    }
    return 'HOSPITAL';
};

const mapStaffUnit = (unit: AdminStaffUnitItem): UserUnitOption => {
    const unitName = resolveObjectValue(unit.unitName) ?? resolveObjectValue(unit.unitCode) ?? '';
    const unitCode = resolveObjectValue(unit.unitCode) ?? unitName;
    return {
        id: unit.id,
        hospitalId: unit.hospitalId ?? '',
        hospitalName: unit.hospitalName ?? '',
        unitCode,
        unitName: unitName || unitCode,
        description: resolveObjectValue(unit.description) ?? null,
        isActive: unit.isActive !== false
    };
};

const mapStaffUnitRange = (unitRange: AdminStaffUnitRangeItem): UserUnitRange => {
    const unitName = resolveObjectValue(unitRange.unitName) ?? '';
    const unitFloor = unitRange.unitFloor?.trim() ?? '';
    const roomStart = unitRange.roomStart ?? 0;
    const roomEnd = unitRange.roomEnd ?? 0;
    const roomRange = unitRange.roomRange?.trim() || `${roomStart}-${roomEnd}`;
    const displayLabel = unitRange.displayLabel?.trim() || (unitFloor ? `${unitFloor}, Rooms ${roomRange}` : roomRange);

    return {
        id: unitRange.id,
        hospitalId: unitRange.hospitalId ?? '',
        hospitalName: unitRange.hospitalName ?? '',
        unitId: unitRange.unitId ?? '',
        unitFloor,
        unitName,
        roomStart,
        roomEnd,
        roomRange,
        displayLabel,
        isActive: unitRange.isActive !== false
    };
};

const mapSupervisorToEmbed = (supervisor: NonNullable<AdminStaffListItem['supervisor']>): AdminUserSupervisorEmbed => ({
    id: supervisor.id,
    email: supervisor.email,
    first_name: supervisor.firstName,
    last_name: supervisor.lastName,
    name: supervisor.displayName
});

const mapStaffListItemToAdminUser = (item: AdminStaffListItem): AdminUser => {
    const organizationKey = resolveOrganizationKey(item);
    const isInternal = organizationKey === 'SILVERSTAY';
    const mappedUnits = Array.isArray(item.units) ? item.units.map(mapStaffUnit) : [];
    const mappedUnitIds = mappedUnits.map(({ id }) => id).filter(Boolean);
    const mappedUnitRanges = Array.isArray(item.unitRanges) ? item.unitRanges.map(mapStaffUnitRange) : [];
    const mappedUnitRangeIds = mappedUnitRanges.map(({ id }) => id).filter(Boolean);
    const mappedHospitals = (item.hospitals ?? []).map(({ id, name, isActive, isDefault }) => ({
        id,
        name,
        isDefault,
        isActive
    }));

    return normalizeAdminUser({
        id: item.id,
        email: item.email ?? '',
        name: item.displayName ?? [item.firstName, item.lastName].filter(Boolean).join(' ').trim(),
        first_name: item.firstName,
        last_name: item.lastName,
        nick_name: resolveObjectValue(item.nickName) ?? null,
        isInternal,
        organizationKey,
        user_type: isInternal ? 'internal' : 'hospital',
        role: item.role?.key ?? item.role?.value ?? item.role?.id ?? '',
        role_id: item.role?.id,
        hospital_id: item.hospitalId ?? item.hospitals?.[0]?.id ?? '',
        hospital_ids: mappedHospitals.map((hospital) => hospital.id).filter((id): id is string => Boolean(id)),
        hospitals: mappedHospitals,
        unit_ids: mappedUnitIds,
        unitRangeIds: mappedUnitRangeIds,
        units: Array.isArray(item.units) ? mappedUnits : (item.units ?? ''),
        unitRanges: mappedUnitRanges,
        source: item.source ?? null,
        title: resolveObjectValue(item.jobTitle) ?? null,
        department: resolveObjectValue(item.department) ?? null,
        mobile_number: resolvePhoneE164(item.mobilePhone),
        office_number: resolvePhoneE164(item.officePhone),
        schedule_type: item.scheduleType?.id ?? item.scheduleType?.value ?? item.scheduleType?.key ?? null,
        schedule_type_value: item.scheduleType?.value ?? null,
        supervisor_user_id: resolveObjectValue(item.supervisorUserId) ?? item.supervisor?.id ?? null,
        supervisor: item.supervisor ? mapSupervisorToEmbed(item.supervisor) : undefined,
        status: item.status ?? null,
        isActive: item.isActive,
        active_since: item.activeSince ?? null
    });
};

const normalizeAdminUser = (user: AdminUser): AdminUser => {
    const displayName = getAdminUserDisplayName(user);
    return {
        ...user,
        name: user.name || displayName
    };
};

const hasText = (value?: string | null): value is string => Boolean(value?.trim());

const hasItems = <T>(value?: T[] | null): value is T[] => Array.isArray(value) && value.length > 0;

/** Preserve list-row fields when PATCH returns a sparse staff body. */
export const mergeAdminUserUpdate = (existing: AdminUser, updated: AdminUser): AdminUser => ({
    ...existing,
    ...updated,
    email: hasText(updated.email) ? updated.email : existing.email,
    name: hasText(updated.name) ? updated.name : existing.name,
    first_name: hasText(updated.first_name) ? updated.first_name : existing.first_name,
    last_name: hasText(updated.last_name) ? updated.last_name : existing.last_name,
    nick_name: updated.nick_name ?? existing.nick_name ?? null,
    role: hasText(updated.role) ? updated.role : existing.role,
    role_id: hasText(updated.role_id) ? updated.role_id : existing.role_id,
    hospital_id: hasText(updated.hospital_id) ? updated.hospital_id : existing.hospital_id,
    hospital_ids: hasItems(updated.hospital_ids) ? updated.hospital_ids : existing.hospital_ids,
    hospitals: hasItems(updated.hospitals) ? updated.hospitals : existing.hospitals,
    unit_ids: hasItems(updated.unit_ids) ? updated.unit_ids : existing.unit_ids,
    units:
        (Array.isArray(updated.units) && updated.units.length > 0) || (typeof updated.units === 'string' && updated.units.trim())
            ? updated.units
            : existing.units,
    unitRangeIds: hasItems(updated.unitRangeIds) ? updated.unitRangeIds : existing.unitRangeIds,
    unitRanges: hasItems(updated.unitRanges) ? updated.unitRanges : existing.unitRanges,
    source: updated.source ?? existing.source,
    title: updated.title ?? existing.title ?? null,
    department: updated.department ?? existing.department ?? null,
    mobile_number: updated.mobile_number ?? existing.mobile_number ?? null,
    office_number: updated.office_number ?? existing.office_number ?? null,
    schedule_type: updated.schedule_type ?? existing.schedule_type ?? null,
    schedule_type_value: updated.schedule_type_value ?? existing.schedule_type_value ?? null,
    supervisor_user_id: updated.supervisor_user_id ?? existing.supervisor_user_id ?? null,
    supervisor: updated.supervisor ?? existing.supervisor,
    status: updated.status ?? existing.status ?? null,
    active_since: updated.active_since ?? existing.active_since ?? null,
    isActive: updated.isActive ?? existing.isActive,
    organizationKey: updated.organizationKey ?? existing.organizationKey,
    isInternal: typeof updated.isInternal === 'boolean' ? updated.isInternal : existing.isInternal,
    user_type: hasText(updated.user_type) ? updated.user_type : existing.user_type
});

const buildAdminStaffPatchRequestBody = (payload: UpdateUserPayload) => {
    const resolvedRoleId = payload.role_id?.trim() ? payload.role_id.trim() : payload.role.trim();
    const hospitalIds = payload.hospitalIds ?? (payload.hospitalId ? [payload.hospitalId] : []);

    const body: Record<string, unknown> = {
        organizationKey: payload.organizationKey,
        firstName: payload.first_name.trim(),
        lastName: payload.last_name.trim(),
        roleId: resolvedRoleId,
        hospitalIds
    };

    if (payload.emailChanged && payload.email.trim()) {
        body.email = payload.email.trim();
    }
    if (payload.nick_name?.trim()) {
        body.nickName = payload.nick_name.trim();
    }
    if (payload.title?.trim()) {
        body.title = payload.title.trim();
    }
    if (payload.department?.trim()) {
        body.department = payload.department.trim();
    }
    if (payload.mobile_number?.trim()) {
        const mobilePhone = phonePayloadToE164(payload.mobile_number);
        if (mobilePhone) {
            body.mobilePhone = mobilePhone;
        }
    }
    if (payload.office_number?.trim()) {
        const officePhone = phonePayloadToE164(payload.office_number);
        if (officePhone) {
            body.officePhone = officePhone;
        }
    }
    if (payload.schedule_type?.trim()) {
        body.scheduleTypeId = payload.schedule_type.trim();
    }
    if (payload.supervisor_user_id?.trim()) {
        body.supervisorUserId = payload.supervisor_user_id.trim();
    }
    if (typeof payload.isActive === 'boolean') {
        body.isActive = payload.isActive;
    }
    if (payload.status?.trim()) {
        body.status = payload.status.trim();
    }
    if (Array.isArray(payload.unit_ids)) {
        body.unitIds = payload.unit_ids;
    }
    if (Array.isArray(payload.unitRangeIds)) {
        body.unitRangeIds = payload.unitRangeIds;
    }

    return body;
};

const buildAdminStaffCreateRequestBody = (payload: CreateUserPayload) => {
    const hospitalIds = payload.hospitalIds ?? (payload.hospitalId ? [payload.hospitalId] : []);

    const body: Record<string, unknown> = {
        organizationKey: payload.organizationKey,
        firstName: payload.first_name,
        lastName: payload.last_name,
        email: payload.email,
        hospitalIds,
        roleId: payload.role
    };

    if (payload.nick_name?.trim()) {
        body.nickName = payload.nick_name.trim();
    }
    if (payload.title?.trim()) {
        body.title = payload.title.trim();
    }
    if (payload.department?.trim()) {
        body.department = payload.department.trim();
    }
    if (payload.mobile_number?.trim()) {
        const mobilePhone = phonePayloadToE164(payload.mobile_number);
        if (mobilePhone) {
            body.mobilePhone = mobilePhone;
        }
    }
    if (payload.office_number?.trim()) {
        const officePhone = phonePayloadToE164(payload.office_number);
        if (officePhone) {
            body.officePhone = officePhone;
        }
    }
    if (payload.schedule_type?.trim()) {
        body.scheduleTypeId = payload.schedule_type.trim();
    }
    if (payload.supervisor_user_id?.trim()) {
        body.supervisorUserId = payload.supervisor_user_id.trim();
    }
    if (Array.isArray(payload.unit_ids)) {
        body.unitIds = payload.unit_ids;
    }

    return body;
};

export type AdminUsersLookupOptions = Pick<FetchAdminUsersParams, 'search' | 'limit' | 'page' | 'organizationKey'>;

/** Staff picker: same source as the admin staff list (`GET /admin/staff`). */
export async function getAdminUsersLookup(hospitalId?: string, options?: AdminUsersLookupOptions): Promise<AdminUser[]> {
    try {
        const queryParams: Record<string, unknown> = {
            page: options?.page ?? 1,
            limit: options?.limit ?? DEFAULT_ROWS_PER_PAGE,
            search: options?.search?.trim() || undefined,
            organizationKey: options?.organizationKey ?? undefined
        };
        if (hospitalId) {
            queryParams.hospitalId = hospitalId;
        }
        const { data } = await axiosInstance.get<AdminStaffListResponse | AdminStaffListItem[]>('/admin/staff', {
            params: queryParams
        });

        if (Array.isArray(data)) {
            return data.map(mapStaffListItemToAdminUser);
        }
        return (data?.items ?? []).map(mapStaffListItemToAdminUser);
    } catch {
        return [];
    }
}

export const fetchAdminRoleOptions = createAsyncThunk<AdminDirectoryOption[], void, { rejectValue: string }>(
    'userAdmin/fetchRoleOptions',
    async () => {
        try {
            const { data } = await axiosInstance.get<AdminDirectoryResponse>('/admin/roles');
            return data?.items ?? [];
        } catch {
            return [];
        }
    }
);

export const fetchAdminScheduleTypeOptions = createAsyncThunk<AdminDirectoryOption[], void, { rejectValue: string }>(
    'userAdmin/fetchScheduleTypeOptions',
    async () => {
        try {
            const { data } = await axiosInstance.get<AdminDirectoryResponse>('/admin/schedule-types');
            return data?.items ?? [];
        } catch {
            return [];
        }
    }
);

export async function getAdminStaffById(staffId: string, hospitalId?: string): Promise<AdminUser | null> {
    const { data } = await axiosInstance.get<AdminStaffDetailResponse | AdminStaffListItem>(`/admin/staff/${staffId}`, {
        params: hospitalId ? { hospitalId } : undefined
    });

    if ('id' in data) {
        return mapStaffListItemToAdminUser(data);
    }
    if (data?.item) {
        return mapStaffListItemToAdminUser(data.item);
    }
    return null;
}

export const fetchAdminUsers = createAsyncThunk<
    {
        items: AdminUser[];
        total: number;
        page: number;
        limit: number;
    },
    FetchAdminUsersParams,
    { rejectValue: string }
>('userAdmin/fetchUsers', async (params) => {
    try {
        const queryParams: Record<string, unknown> = {
            page: params.page,
            limit: params.limit,
            search: params.search || undefined
        };
        if (params.hospitalId) {
            queryParams.hospitalId = params.hospitalId;
        }
        const { data } = await axiosInstance.get<AdminStaffListResponse | AdminStaffListItem[]>('/admin/staff', {
            params: queryParams
        });

        if (Array.isArray(data)) {
            return {
                items: data.map(mapStaffListItemToAdminUser),
                total: data.length,
                page: params.page ?? 1,
                limit: params.limit ?? data.length ?? DEFAULT_ROWS_PER_PAGE
            };
        }

        return {
            items: (data?.items ?? []).map(mapStaffListItemToAdminUser),
            total: data?.total ?? 0,
            page: data?.page ?? params.page ?? 1,
            limit: data?.limit ?? params.limit ?? DEFAULT_ROWS_PER_PAGE
        };
    } catch {
        return {
            items: [],
            total: 0,
            page: params.page ?? 1,
            limit: params.limit ?? DEFAULT_ROWS_PER_PAGE
        };
    }
});

export const createUser = createAsyncThunk<AdminUser, CreateUserPayload, { rejectValue: string }>(
    'userAdmin/createUser',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post<CreateAdminStaffResponse>('/admin/staff', buildAdminStaffCreateRequestBody(payload));
            const isInternal = payload.organizationKey === 'SILVERSTAY';
            return normalizeAdminUser({
                id: data.id,
                email: payload.email,
                name: payload.name,
                first_name: payload.first_name,
                last_name: payload.last_name,
                nick_name: payload.nick_name,
                isInternal,
                organizationKey: payload.organizationKey,
                user_type: isInternal ? 'internal' : 'hospital',
                role: payload.role,
                role_id: payload.role.trim(),
                hospital_id: payload.hospitalId,
                hospital_ids: payload.hospitalIds,
                unit_ids: payload.unit_ids,
                title: payload.title,
                department: payload.department,
                mobile_number: payload.mobile_number,
                office_number: payload.office_number,
                schedule_type: payload.schedule_type,
                supervisor_user_id: payload.supervisor_user_id,
                isActive: true,
                status: 'Active',
                active_since: new Date().toISOString()
            });
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to create user'));
        }
    }
);

export const updateUser = createAsyncThunk<AdminUser, UpdateUserPayload, { rejectValue: string }>(
    'userAdmin/updateUser',
    async (payload, { rejectWithValue }) => {
        try {
            const params = payload.queryHospitalId ? { hospitalId: payload.queryHospitalId } : undefined;
            const requestBody = buildAdminStaffPatchRequestBody(payload);
            await axiosInstance.patch<AdminStaffDetailResponse | AdminStaffListItem>(`/admin/staff/${payload.id}`, requestBody, { params });

            // Email is a login identity — skip refetching detail after change; callers redirect away.
            if (!payload.emailChanged) {
                const hospitalId = payload.queryHospitalId ?? payload.hospitalId;
                const refetched = await getAdminStaffById(payload.id, hospitalId || undefined);
                if (refetched) {
                    return refetched;
                }
            }

            const isInternal = payload.organizationKey === 'SILVERSTAY';
            return normalizeAdminUser({
                id: payload.id,
                email: payload.email,
                name: payload.name,
                first_name: payload.first_name,
                last_name: payload.last_name,
                nick_name: payload.nick_name,
                isInternal,
                organizationKey: payload.organizationKey,
                user_type: isInternal ? 'internal' : 'hospital',
                role: payload.role,
                role_id: payload.role_id?.trim() || payload.role.trim(),
                hospital_id: payload.hospitalId,
                hospital_ids: payload.hospitalIds,
                unit_ids: payload.unit_ids ?? [],
                unitRangeIds: payload.unitRangeIds ?? [],
                title: payload.title,
                department: payload.department,
                mobile_number: payload.mobile_number,
                office_number: payload.office_number,
                schedule_type: payload.schedule_type,
                supervisor_user_id: payload.supervisor_user_id,
                isActive: payload.isActive,
                status: payload.status
            });
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to update user'));
        }
    }
);

export const reinviteStaff = createAsyncThunk<void, ReinviteStaffPayload, { rejectValue: string }>(
    'userAdmin/reinviteStaff',
    async ({ staffId }, { rejectWithValue }) => {
        try {
            await axiosInstance.post(`/admin/staff/${staffId}/reinvite`);
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to reinvite staff member'));
        }
    }
);

/** `POST /admin/staff/bulk-import` — multipart `file`. */
export const STAFF_CSV_IMPORT_ENDPOINT = '/admin/staff/bulk-import';

export const importStaffCsv = createAsyncThunk<ImportStaffCsvResponse, ImportStaffCsvPayload, { rejectValue: string }>(
    'userAdmin/importStaffCsv',
    async ({ file }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const headers = new AxiosHeaders();
            headers.setContentType(false);
            const { data } = await axiosInstance.post<ImportStaffCsvResponse>(STAFF_CSV_IMPORT_ENDPOINT, formData, { headers });
            return data;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to upload staff import file'));
        }
    }
);
